import React, { useState } from 'react';
import { MapIcon, MaximizeIcon, MapPinIcon, ClockIcon, BanknoteIcon, ArrowRightIcon, MapPinnedIcon } from 'lucide-react';
import { StatsCard } from '../components/StatsCard';
import { CertificateCard } from '../components/CertificateCard';
import { LandCard } from '../components/LandCard';
import { AlertItem } from '../components/AlertItem';
import { ownerData, notificationsData } from '../data/sampleData';
interface DashboardPageProps {
  navigate: (page: string, landId?: string) => void;
  t: (key: string) => string;
  lands: any[];
}
export function DashboardPage({
  navigate,
  t,
  lands
}: DashboardPageProps) {
  const unreadAlerts = notificationsData.filter(n => !n.read).slice(0, 3);
  const [activeMapPin, setActiveMapPin] = useState<string | null>(null);
  // Calculate dynamic stats based on lands array
  const totalAreaVal = lands.reduce((acc, land) => {
    const val = parseFloat(land.area.split(' ')[0]);
    return acc + (isNaN(val) ? 0 : val);
  }, 0);
  const uniqueDistricts = Array.from(new Set(lands.map(l => l.district)));
  return <div className="space-y-6 pb-20 lg:pb-6" data-id="element-254">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2" data-id="element-255">
        <div data-id="element-256">
          <h2 className="text-2xl font-bold text-slate-800" data-id="element-257">
            {t('common.welcome')}, {ownerData.name}
          </h2>
          <p className="text-slate-500" data-id="element-258">{t('app.subtitle')}</p>
        </div>
        <div className="flex gap-3" data-id="element-259">
          <button onClick={() => navigate('services')} className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 px-4 py-2 rounded-lg font-medium text-sm transition-colors shadow-sm" data-id="element-260">
            {t('nav.services')}
          </button>
          <button onClick={() => navigate('services')} className="bg-[#006A4E] text-white hover:bg-[#00523c] px-4 py-2 rounded-lg font-medium text-sm transition-colors shadow-sm flex items-center gap-2" data-id="element-261">
            <BanknoteIcon className="w-4 h-4" data-id="element-262" /> {t('status.paid')} Tax
          </button>
        </div>
      </div>

      {/* Top Row: Stats & Certificate */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-263">
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-264">
          <StatsCard title={t('land.totalLands')} value={lands.length} icon={<MapIcon className="w-6 h-6" data-id="element-266" />} onClick={() => navigate('mylands')} data-id="element-265" />
          <StatsCard title={t('land.totalArea')} value={`${totalAreaVal} decimals`} icon={<MaximizeIcon className="w-6 h-6" data-id="element-268" />} iconBgColor="bg-blue-50" iconColor="text-blue-600" data-id="element-267" />
          <StatsCard title={t('land.districts')} value={uniqueDistricts.length} subtitle={uniqueDistricts.join(', ')} icon={<MapPinIcon className="w-6 h-6" data-id="element-270" />} iconBgColor="bg-purple-50" iconColor="text-purple-600" data-id="element-269" />
          <StatsCard title={t('dashboard.pendingServices')} value={2} icon={<ClockIcon className="w-6 h-6" data-id="element-272" />} iconBgColor="bg-amber-50" iconColor="text-amber-600" onClick={() => navigate('services')} data-id="element-271" />
        </div>
        <div className="lg:col-span-1" data-id="element-273">
          <CertificateCard onClick={() => navigate('certificate')} t={t} data-id="element-274" />
        </div>
      </div>

      {/* Map View Section */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-275">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center" data-id="element-276">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2" data-id="element-277">
            <MapPinnedIcon className="w-5 h-5 text-[#006A4E]" data-id="element-278" />{' '}
            {t('dashboard.mapView')}
          </h3>
        </div>
        <div className="relative w-full h-80 bg-slate-100 overflow-hidden" data-id="element-279">
          {/* Simulated Map Background */}
          <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(#006A4E 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }} data-id="element-280"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-transparent to-slate-200/50" data-id="element-281"></div>

          {/* Map Pins based on lands data */}
          {lands.map((land, index) => {
          // Generate pseudo-random positions based on index for prototype
          const top = `${20 + index * 25 % 60}%`;
          const left = `${20 + index * 35 % 60}%`;
          const isActive = activeMapPin === land.id;
          return <div key={land.id} className="absolute" style={{
            top,
            left
          }} data-id="element-282">
                <button onClick={() => setActiveMapPin(isActive ? null : land.id)} className={`relative z-10 transform transition-transform hover:scale-110 ${isActive ? 'scale-125' : ''}`} data-id="element-283">
                  <MapPinIcon className={`w-8 h-8 ${isActive ? 'text-[#C8102E]' : 'text-[#006A4E] drop-shadow-md'}`} data-id="element-284" />
                </button>

                {/* Mini Card Popup */}
                {isActive && <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-white rounded-lg shadow-xl border border-slate-200 p-3 z-20 animate-in fade-in slide-in-from-bottom-2" data-id="element-285">
                    <p className="font-bold text-slate-800 text-sm" data-id="element-286">
                      {land.id}
                    </p>
                    <p className="text-xs text-slate-500 mb-2" data-id="element-287">
                      {land.mouza}, {land.district}
                    </p>
                    <div className="grid grid-cols-2 gap-1 text-[10px] mb-2 bg-slate-50 p-1.5 rounded" data-id="element-288">
                      <div data-id="element-289">
                        <span className="text-slate-400" data-id="element-290">
                          {t('land.khatian')}:
                        </span>{' '}
                        <span className="font-medium" data-id="element-291">{land.khatian}</span>
                      </div>
                      <div data-id="element-292">
                        <span className="text-slate-400" data-id="element-293">{t('land.dag')}:</span>{' '}
                        <span className="font-medium" data-id="element-294">{land.plot}</span>
                      </div>
                    </div>
                    <button onClick={() => navigate('landdetails', land.id)} className="w-full text-center text-xs bg-[#006A4E] text-white py-1.5 rounded hover:bg-[#00523c] transition-colors" data-id="element-295">
                      {t('common.viewDetails')}
                    </button>
                    {/* Triangle pointer */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-white" data-id="element-296"></div>
                  </div>}
              </div>;
        })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-297">
        {/* Recent Lands */}
        <div className="lg:col-span-2 space-y-4" data-id="element-298">
          <div className="flex items-center justify-between" data-id="element-299">
            <h3 className="text-lg font-bold text-slate-800" data-id="element-300">
              {t('land.recentLands')}
            </h3>
            <button onClick={() => navigate('mylands')} className="text-sm text-[#006A4E] font-medium hover:underline flex items-center gap-1" data-id="element-301">
              {t('common.all')} <ArrowRightIcon className="w-4 h-4" data-id="element-302" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-303">
            {lands.slice(0, 2).map(land => <LandCard key={land.id} land={land} onClick={() => navigate('landdetails', land.id)} t={t} data-id="element-304" />)}
          </div>
        </div>

        {/* Action Needed / Alerts */}
        <div className="space-y-4" data-id="element-305">
          <div className="flex items-center justify-between" data-id="element-306">
            <h3 className="text-lg font-bold text-slate-800" data-id="element-307">
              {t('common.actionNeeded')}
            </h3>
            <button onClick={() => navigate('notifications')} className="text-sm text-[#006A4E] font-medium hover:underline" data-id="element-308">
              {t('common.all')}
            </button>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 space-y-3" data-id="element-309">
            {unreadAlerts.map(alert => <AlertItem key={alert.id} type={alert.type} title={alert.title} message={alert.message} date={alert.date} read={alert.read} onClick={() => {
            if (alert.type === 'notice' || alert.type === 'mutation') navigate('services');else if (alert.type === 'dispute') navigate('mylands');else navigate('notifications');
          }} data-id="element-310" />)}
          </div>
        </div>
      </div>
    </div>;
}